import React, { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  TextField,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Chat as ChatIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from './ConfirmDialog';
import { Conversation } from '../../types';

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string) => Promise<void>;
  onUpdate: (conversationId: string, title: string) => Promise<void>;
  formatDate: (dateString: string) => string;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  onSelect,
  onDelete,
  onUpdate,
  formatDate,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditStart = () => {
    setIsEditing(true);
    setEditTitle(conversation.title);
    handleMenuClose();
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditTitle(conversation.title);
  };

  const handleEditSave = async () => {
    if (editTitle.trim() && editTitle !== conversation.title) {
      setIsUpdating(true);
      try {
        await onUpdate(conversation.id, editTitle.trim());
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to update conversation:', error);
        // Optionally show error message
      } finally {
        setIsUpdating(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleDeleteStart = () => {
    setShowDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete(conversation.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Optionally show error message
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleEditSave();
    } else if (event.key === 'Escape') {
      handleEditCancel();
    }
  };

  return (
    <>
      <ListItem 
        disablePadding
        secondaryAction={
          !isEditing && (
            <IconButton
              edge="end"
              onClick={handleMenuClick}
              size="small"
              sx={{ opacity: isSelected ? 1 : 0.7 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )
        }
      >
        {isEditing ? (
          <Box sx={{ width: '100%', px: 2, py: 1 }}>
            <TextField
              fullWidth
              size="small"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t('conversations.editPlaceholder')}
              autoFocus
              disabled={isUpdating}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                    <Tooltip title={t('common.save')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleEditSave}
                          disabled={isUpdating || !editTitle.trim()}
                          color="primary"
                        >
                          {isUpdating ? (
                            <CircularProgress size={16} />
                          ) : (
                            <CheckIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('common.cancel')}>
                      <IconButton
                        size="small"
                        onClick={handleEditCancel}
                        disabled={isUpdating}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
              }}
            />
          </Box>
        ) : (
          <ListItemButton
            selected={isSelected}
            onClick={() => onSelect(conversation.id)}
            disabled={isDeleting}
          >
            <ListItemIcon>
              {isDeleting ? (
                <CircularProgress size={20} />
              ) : (
                <ChatIcon />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ 
                    fontWeight: isSelected ? 600 : 400,
                    opacity: isDeleting ? 0.5 : 1,
                  }}
                >
                  {conversation.title}
                </Typography>
              }
              secondary={
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ opacity: isDeleting ? 0.5 : 1 }}
                >
                  {conversation.message_count} {conversation.message_count === 1 ? t('conversations.message') : t('conversations.messages')} • {formatDate(conversation.updated_at)}
                </Typography>
              }
            />
          </ListItemButton>
        )}
      </ListItem>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 160 }
        }}
      >
        <MenuItem onClick={handleEditStart}>
          <EditIcon sx={{ mr: 2 }} fontSize="small" />
          {t('conversations.edit')}
        </MenuItem>
        <MenuItem onClick={handleDeleteStart} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 2 }} fontSize="small" />
          {t('conversations.delete')}
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title={t('conversations.deleteConfirmTitle')}
        message={t('conversations.deleteConfirmMessage')}
        confirmText={t('common.delete')}
        severity="error"
        isLoading={isDeleting}
      />
    </>
  );
};

export default ConversationItem;
